// src/components/goal-settings/KPIList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    listKpis,
    createKpi,
    updateKpi,
    deleteKpi,
    fetchManagerKpisForKpa,
    cascadeKpisIntoKpa,
} from '../../api/kpis';

import KpiCreateModal from './KpiCreateModal';
import KpiEditModal from './KpiEditModal';
import KpiCascadeModal from './KpiCascadeModal';

function asNum(v, d = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
}

export default function KPIList({
                                    kpa,                   // { id, name, weight, parent_id? ... }
                                    canEdit = true,        // false for manager viewing employee
                                }) {
    const kpaId = kpa?.id;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    // modals
    const [showCreate, setShowCreate] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [showCascade, setShowCascade] = useState(false);

    async function refresh() {
        if (!kpaId) return;
        setLoading(true); setErr('');
        try {
            const data = await listKpis(kpaId);
            setRows(data || []);
        } catch (e) {
            setErr(e?.response?.data?.message || 'Failed to load KPIs.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refresh(); }, [kpaId]);

    const totalWeight = useMemo(() => rows.reduce((s, r) => s + asNum(r.weight), 0), [rows]);

    // CRUD
    const onCreate = async (payload) => {
        await createKpi({ ...payload, key_performance_area_id: kpaId });
        setShowCreate(false);
        await refresh();
    };

    const onUpdate = async (id, patch) => {
        await updateKpi(id, patch);
        setEditRow(null);
        await refresh();
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this KPI?')) return;
        await deleteKpi(id);
        await refresh();
    };

    // Cascade
    const onCascadeSubmit = async ({ kpi_ids }) => {
        await cascadeKpisIntoKpa(kpaId, { kpi_ids });
        setShowCascade(false);
        await refresh();
    };

    const fmtValue = (row, field) => {
        const val = row?.[field];
        if (val == null || val === '') return '—';
        if (row?.is_percentage) return `${val}%`;
        const unit = row?.unit ? ` ${row.unit}` : '';
        return `${val}${unit}`;
    };

    return (
        <div className="mt-2">
            <div className="d-flex align-items-center mb-2">
                <h6 className="mb-0">KPIs for: <span className="text-muted">{kpa?.name}</span></h6>
                <div className="ms-auto d-flex gap-2">
                    {canEdit && (
                        <>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowCreate(true)}>
                                Add KPI
                            </button>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowCascade(true)}>
                                Cascade KPIs
                            </button>
                        </>
                    )}
                </div>
            </div>

            {err && <div className="alert alert-danger py-1">{err}</div>}

            <div className="table-responsive">
                <table className="table table-sm align-middle">
                    <thead>
                    <tr>
                        <th style={{width: 80}}>KPI #</th>
                        <th>Name</th>
                        <th style={{width: 140, textAlign:'right'}}>Threshold</th>
                        <th style={{width: 140, textAlign:'right'}}>Target</th>
                        <th style={{width: 140, textAlign:'right'}}>Stretch</th>
                        <th style={{width: 100, textAlign:'right'}}>Weight</th>
                        {canEdit && <th style={{width: 150}} className="text-end">Actions</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {loading && (
                        <tr><td colSpan={canEdit ? 7 : 6} className="text-center text-muted">Loading…</td></tr>
                    )}

                    {!loading && rows.length === 0 && (
                        <tr><td colSpan={canEdit ? 7 : 6} className="text-center text-muted">No KPIs yet.</td></tr>
                    )}

                    {!loading && rows.map((r, idx) => {
                        const counter = `KPI ${String(idx + 1).padStart(2, '0')}`;
                        const cascaded = Boolean(r.parent_id);

                        return (
                            <tr key={r.id}>
                                <td>{counter}{cascaded && <span className="badge bg-secondary ms-2">Cascaded</span>}</td>
                                <td>{r.name}</td>
                                <td style={{textAlign:'right'}}>{fmtValue(r, 'threshold')}</td>
                                <td style={{textAlign:'right'}}>{fmtValue(r, 'target')}</td>
                                <td style={{textAlign:'right'}}>{fmtValue(r, 'stretch')}</td>
                                <td style={{textAlign:'right'}}>{r.weight ?? '—'}</td>
                                {canEdit && (
                                    <td className="text-end">
                                        <div className="d-inline-flex gap-1">
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditRow(r)}>Edit</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(r.id)}>Delete</button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    </tbody>

                    <tfoot>
                    <tr>
                        <td colSpan={5} className="text-end fw-semibold">KPI Total</td>
                        <td style={{textAlign:'right'}} className="fw-semibold">{totalWeight}</td>
                        {canEdit && <td></td>}
                    </tr>
                    </tfoot>
                </table>
            </div>

            {/* CREATE */}
            <KpiCreateModal
                show={showCreate}
                kpaId={kpaId}
                onClose={() => setShowCreate(false)}
                onCreated={onCreate}
            />

            {/* EDIT */}
            <KpiEditModal
                show={!!editRow}
                kpi={editRow}
                onClose={() => setEditRow(null)}
                onSaved={(patch) => editRow?.id && onUpdate(editRow.id, patch)}
            />

            {/* CASCADE */}
            <KpiCascadeModal
                show={showCascade}
                kpaId={kpaId}
                onClose={() => setShowCascade(false)}
                onSubmit={onCascadeSubmit}
                fetchManagerKpis={fetchManagerKpisForKpa}
            />
        </div>
    );
}